export function normalizePhoneNumber(input) {
  if (input == null) return ''

  const trimmed = String(input).trim()
  let digits

  if (/^\+91/.test(trimmed)) {
    digits = trimmed.slice(3).replace(/\D/g, '')
  } else if (trimmed.startsWith('+')) {
    digits = trimmed.slice(1).replace(/\D/g, '')
  } else {
    digits = trimmed.replace(/\D/g, '')
  }

  if (digits.length > 10 && digits.startsWith('91')) {
    digits = digits.slice(2)
  }

  if (digits.length > 10) {
    digits = digits.slice(-10)
  }

  return digits.slice(0, 10)
}

export function parseMobileDigitLimits(mobileDigits) {
  const raw = String(mobileDigits || '').trim()
  if (!raw || raw.toUpperCase() === 'N/A') {
    return { min: 6, max: 15 }
  }

  if (raw.includes('-')) {
    const [minStr, maxStr] = raw.split('-')
    const min = parseInt(minStr, 10)
    const max = parseInt(maxStr, 10)
    if (Number.isFinite(min) && Number.isFinite(max)) {
      return { min, max }
    }
  }

  const fixed = parseInt(raw, 10)
  if (Number.isFinite(fixed)) {
    return { min: fixed, max: fixed }
  }

  return { min: 6, max: 15 }
}

export function normalizeLocalPhoneDigits(input, maxLength) {
  const digits = String(input ?? '').replace(/\D/g, '')
  const max = Number.isFinite(maxLength) && maxLength > 0 ? maxLength : 15
  return digits.slice(0, max)
}

export function buildFullPhoneNumber(callingCode, localDigits) {
  const code = String(callingCode || '').trim()
  const digits = String(localDigits || '').replace(/\D/g, '')
  if (!digits) return ''
  if (!code) return digits
  return code.startsWith('+') ? `${code}${digits}` : `+${code}${digits}`
}

export function formatPhoneDigitHint(limits) {
  if (!limits) return ''
  if (limits.min === limits.max) {
    return `${limits.max} digits`
  }
  return `${limits.min}-${limits.max} digits`
}

export function parseFullPhoneNumber(phoneNumber, countries = []) {
  const trimmed = String(phoneNumber || '').trim()
  if (!trimmed) {
    return { iso2: 'IN', localDigits: '', callingCode: '+91' }
  }

  const hasPlus = trimmed.startsWith('+')
  const allDigits = (hasPlus ? trimmed.slice(1) : trimmed).replace(/\D/g, '')
  if (!allDigits) {
    return { iso2: 'IN', localDigits: '', callingCode: '+91' }
  }

  const sortedCountries = [...countries].sort(
    (a, b) =>
      String(b.calling_code || '').replace(/\D/g, '').length -
      String(a.calling_code || '').replace(/\D/g, '').length
  )

  const shouldTryCountrySplit = hasPlus || allDigits.length > 10

  if (shouldTryCountrySplit) {
    for (const country of sortedCountries) {
      const codeDigits = String(country.calling_code || '').replace(/\D/g, '')
      if (!codeDigits || !allDigits.startsWith(codeDigits)) continue

      const localDigits = allDigits.slice(codeDigits.length)
      if (!localDigits) continue

      const limits = parseMobileDigitLimits(country.mobile_digits)
      if (localDigits.length >= limits.min && localDigits.length <= limits.max) {
        return {
          iso2: country.iso2,
          localDigits,
          callingCode: country.calling_code
        }
      }
    }

    for (const country of sortedCountries) {
      const codeDigits = String(country.calling_code || '').replace(/\D/g, '')
      if (!codeDigits || !allDigits.startsWith(codeDigits)) continue

      const localDigits = allDigits.slice(codeDigits.length)
      if (!localDigits) continue

      return {
        iso2: country.iso2,
        localDigits,
        callingCode: country.calling_code
      }
    }
  }

  if (allDigits.length <= 10) {
    return { iso2: 'IN', localDigits: allDigits, callingCode: '+91' }
  }

  return { iso2: 'IN', localDigits: allDigits, callingCode: '+91' }
}

export function applyPhoneInputValue(input, countries, fallbackIso = 'IN') {
  const parsed = parseFullPhoneNumber(input, countries)
  const country =
    countries.find((item) => item.iso2 === parsed.iso2) ||
    countries.find((item) => item.iso2 === fallbackIso) ||
    countries[0] ||
    null
  const limits = parseMobileDigitLimits(country?.mobile_digits)

  return {
    iso2: country?.iso2 || parsed.iso2 || fallbackIso,
    localDigits: normalizeLocalPhoneDigits(parsed.localDigits, limits.max),
    callingCode: country?.calling_code || parsed.callingCode
  }
}
